import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Switch,
  confirm,
  toast,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { ArrowRightIcon, PlusIcon, SparklesIcon, Trash2Icon } from "lucide-react";
import { useState } from "react";
import {
  useCreateVisitorPrizeMutation,
  useDeferredDrawMutation,
  useDeleteVisitorPrizeMutation,
  useInitializeVisitorLotteryMutation,
  useUpdateVisitorLotteryEventMutation,
  useUpdateVisitorPrizeMutation,
  useVisitorLotteryEventQuery,
  useVisitorLotteryEventsQuery,
} from "../api/queries";
import { VisitorRegistrationQrCard } from "../ui/VisitorRegistrationQrCard";

const STATUS_LABELS = {
  draft: "Draft",
  open: "Open",
  closed: "Closed",
} as const;

export function VisitorLotteryEventPage() {
  const nav = useNavigate();
  const canWrite = useActionPermission(["visitor-lottery:write"]);
  const { data: eventsData, isLoading: eventsLoading } =
    useVisitorLotteryEventsQuery();
  const event = eventsData?.items[0];
  const initMutation = useInitializeVisitorLotteryMutation();
  const { data: detail, isLoading: detailLoading } = useVisitorLotteryEventQuery(
    event?.id,
  );
  const updateMutation = useUpdateVisitorLotteryEventMutation();
  const createPrizeMutation = useCreateVisitorPrizeMutation();
  const updatePrizeMutation = useUpdateVisitorPrizeMutation();
  const deletePrizeMutation = useDeleteVisitorPrizeMutation();
  const deferredDrawMutation = useDeferredDrawMutation();

  const [newPrizeName, setNewPrizeName] = useState("");
  const [newPrizeQty, setNewPrizeQty] = useState("10");
  const [newPrizeWeight, setNewPrizeWeight] = useState("10");
  const [newPrizeMode, setNewPrizeMode] = useState<"instant" | "deferred">(
    "instant",
  );

  const handleInit = async () => {
    try {
      const result = await initMutation.mutateAsync();
      toast.success(
        result.eventCreated
          ? "ສ້າງ Visitor Lottery 2026 ແລ້ວ"
          : "Event ມີຢູ່ແລ້ວ — ອັບເດດຂໍ້ຄວາມແລ້ວ",
      );
    } catch {
      toast.error("ບໍ່ສາມາດ initialize ໄດ້");
    }
  };

  const handleStatusChange = async (status: "draft" | "open" | "closed") => {
    if (!event?.id) return;
    try {
      await updateMutation.mutateAsync({ id: event.id, body: { status } });
      toast.success("ອັບເດດສະຖານະແລ້ວ");
    } catch {
      toast.error("ອັບເດດສະຖານະບໍ່ສຳເລັດ");
    }
  };

  const handleCreatePrize = async () => {
    if (!event?.id || !newPrizeName.trim()) return;
    try {
      await createPrizeMutation.mutateAsync({
        eventId: event.id,
        body: {
          name: newPrizeName.trim(),
          quantity: Number(newPrizeQty) || 0,
          probabilityWeight: Number(newPrizeWeight) || 1,
          isConsolation: false,
          drawMode: newPrizeMode,
          enabled: true,
          sortOrder: (detail?.prizes?.length ?? 0) + 1,
        },
      });
      setNewPrizeName("");
      toast.success("ເພີ່ມລາງວັນແລ້ວ");
    } catch {
      toast.error("ເພີ່ມລາງວັນບໍ່ສຳເລັດ");
    }
  };

  const handleDeletePrize = async (prize: {
    id: string;
    name: string;
    claimed?: number;
  }) => {
    if (!detail?.id) return;
    const claimed = prize.claimed ?? 0;
    if (claimed > 0) {
      toast.error(
        `ລຶບບໍ່ໄດ້ — ມີຜູ້ຖືກລາງວັນແລ້ວ ${claimed} ຄົນ (ປິດເປີດໄດ້ແທນ)`,
      );
      return;
    }
    const ok = await confirm({
      title: "ລຶບລາງວັນ",
      description: `ຢືນຢັນລຶບ「${prize.name}」? ການກະທຳນີ້ຍ້ອນກັບບໍ່ໄດ້`,
    });
    if (!ok) return;
    try {
      await deletePrizeMutation.mutateAsync({
        eventId: detail.id,
        prizeId: prize.id,
      });
      toast.success("ລຶບລາງວັນແລ້ວ");
    } catch {
      toast.error("ລຶບລາງວັນບໍ່ສຳເລັດ");
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "Events", href: "/app/visitor-lottery" },
            { label: "Visitor Lottery" },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">ຕັ້ງຄ່າ Visitor Lottery</h1>
            <p className="text-muted-foreground text-sm">
              Instant win · 1 ເບີ = 1 ສິດ · ຕັ້ງລາງວັນໃນ Admin
            </p>
          </div>
          <div className="flex gap-2">
            {canWrite ? (
              <Button
                variant="outline"
                onClick={handleInit}
                disabled={initMutation.isPending}
              >
                <SparklesIcon className="mr-2 size-4" />
                Initialize 2026
              </Button>
            ) : null}
            {event?.id ? (
              <>
                <Button
                  variant="outline"
                  onClick={() =>
                    nav({ to: "/app/visitor-lottery/scanner" })
                  }
                >
                  Scanner
                </Button>
                <Button
                  variant="outline"
                  onClick={() => nav({ to: "/app/visitor-lottery/report" })}
                >
                  ລາຍງານ
                </Button>
                <Button
                  onClick={() =>
                    nav({
                      to: "/app/visitor-lottery/registrations",
                      search: { eventId: event.id },
                    })
                  }
                >
                  ເບິ່ງລາຍຊື່
                  <ArrowRightIcon className="ml-2 size-4" />
                </Button>
              </>
            ) : null}
          </div>
        </div>

        {eventsLoading || detailLoading ? (
          <p className="text-muted-foreground">ກຳລັງໂຫຼດ...</p>
        ) : !event || !detail ? (
          <Card>
            <CardHeader>
              <CardTitle>ຍັງບໍ່ມີ Event</CardTitle>
              <CardDescription>
                ກົດ Initialize ເພື່ອສ້າງ summer-visitor-2026 + ລາງວັນຕົວຢ່າງ
              </CardDescription>
            </CardHeader>
          </Card>
        ) : (
          <div className="grid gap-6">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {[
                {
                  title: "ລົງທະບຽນທັງໝົດ",
                  value: detail.stats?.totalRegistrations ?? 0,
                },
                {
                  title: "ຖືກລາງວັນ",
                  value: detail.stats?.prizeWins ?? 0,
                },
                {
                  title: "ຍັງບໍ່ຮັບຂອງ",
                  value: detail.stats?.pendingRedeem ?? 0,
                },
                {
                  title: "ລາງວັນເຫຼືອ",
                  value: detail.stats?.prizeRemainingTotal ?? 0,
                },
              ].map((stat) => (
                <Card key={stat.title}>
                  <CardHeader className="pb-2">
                    <CardDescription>{stat.title}</CardDescription>
                    <CardTitle className="text-3xl font-semibold tabular-nums">
                      {stat.value}
                    </CardTitle>
                  </CardHeader>
                </Card>
              ))}
            </div>

            <Card>
              <CardHeader>
                <CardTitle>{detail.title}</CardTitle>
                <CardDescription>slug: {detail.slug}</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>ສະຖານະ</Label>
                  <SimpleSelect
                    value={detail.status}
                    onValueChange={(v) =>
                      handleStatusChange(v as "draft" | "open" | "closed")
                    }
                    disabled={!canWrite || updateMutation.isPending}
                    options={[
                      { value: "draft", label: STATUS_LABELS.draft },
                      { value: "open", label: STATUS_LABELS.open },
                      { value: "closed", label: STATUS_LABELS.closed },
                    ]}
                  />
                </div>
                <div className="flex items-end">
                  <Badge
                    variant={
                      detail.status === "open"
                        ? "default"
                        : detail.status === "draft"
                          ? "secondary"
                          : "outline"
                    }
                  >
                    {STATUS_LABELS[detail.status]}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            <VisitorRegistrationQrCard />

            <Card>
              <CardHeader>
                <CardTitle>ລາງວັນ</CardTitle>
                <CardDescription>
                  weight ກຳນົດໂອກາດ · remaining ຫຼຸດເມື່ອຖືກສຸ່ມໄດ້
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-left">
                      <tr>
                        <th className="p-2">ຊື່</th>
                        <th className="p-2">ຈຳນວນ</th>
                        <th className="p-2">ເຫຼືອ</th>
                        <th className="p-2">Weight</th>
                        <th className="p-2">ເປີດ</th>
                        <th className="p-2">ໂໝດ</th>
                        <th className="p-2">ປະເພດ</th>
                        <th className="p-2 text-right">ຈັດການ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.prizes.map((prize) => (
                        <tr key={prize.id} className="border-t">
                          <td className="p-2">
                            {canWrite ? (
                              <Input
                                className="h-8 min-w-40"
                                defaultValue={prize.name}
                                onBlur={async (e) => {
                                  const name = e.target.value.trim();
                                  if (!name || name === prize.name) {
                                    if (!name) e.target.value = prize.name;
                                    return;
                                  }
                                  try {
                                    await updatePrizeMutation.mutateAsync({
                                      eventId: detail.id,
                                      prizeId: prize.id,
                                      body: { name },
                                    });
                                    toast.success("ອັບເດດຊື່ແລ້ວ");
                                  } catch {
                                    toast.error("ອັບເດດຊື່ບໍ່ສຳເລັດ");
                                    e.target.value = prize.name;
                                  }
                                }}
                              />
                            ) : (
                              <span className="font-medium">{prize.name}</span>
                            )}
                          </td>
                          <td className="p-2">
                            {canWrite && !prize.isConsolation ? (
                              <Input
                                className="h-8 w-20"
                                type="number"
                                min={prize.claimed ?? 0}
                                defaultValue={prize.quantity}
                                onBlur={async (e) => {
                                  const quantity = Number(e.target.value);
                                  if (
                                    Number.isNaN(quantity) ||
                                    quantity === prize.quantity
                                  ) {
                                    return;
                                  }
                                  try {
                                    await updatePrizeMutation.mutateAsync({
                                      eventId: detail.id,
                                      prizeId: prize.id,
                                      body: { quantity },
                                    });
                                    toast.success("ອັບເດດຈຳນວນແລ້ວ");
                                  } catch {
                                    toast.error("ອັບເດດຈຳນວນບໍ່ສຳເລັດ");
                                    e.target.value = String(prize.quantity);
                                  }
                                }}
                              />
                            ) : (
                              prize.quantity
                            )}
                          </td>
                          <td className="p-2">{prize.remaining}</td>
                          <td className="p-2">
                            {canWrite ? (
                              <Input
                                className="h-8 w-20"
                                type="number"
                                min={1}
                                defaultValue={prize.probabilityWeight}
                                onBlur={async (e) => {
                                  const probabilityWeight = Number(
                                    e.target.value,
                                  );
                                  if (
                                    Number.isNaN(probabilityWeight) ||
                                    probabilityWeight < 1 ||
                                    probabilityWeight ===
                                      prize.probabilityWeight
                                  ) {
                                    return;
                                  }
                                  try {
                                    await updatePrizeMutation.mutateAsync({
                                      eventId: detail.id,
                                      prizeId: prize.id,
                                      body: { probabilityWeight },
                                    });
                                    toast.success("ອັບເດດ weight ແລ້ວ");
                                  } catch {
                                    toast.error("ອັບເດດ weight ບໍ່ສຳເລັດ");
                                    e.target.value = String(
                                      prize.probabilityWeight,
                                    );
                                  }
                                }}
                              />
                            ) : (
                              prize.probabilityWeight
                            )}
                          </td>
                          <td className="p-2">
                            <Switch
                              checked={prize.enabled}
                              disabled={!canWrite}
                              onCheckedChange={async (enabled) => {
                                try {
                                  await updatePrizeMutation.mutateAsync({
                                    eventId: detail.id,
                                    prizeId: prize.id,
                                    body: { enabled },
                                  });
                                } catch {
                                  toast.error("ອັບເດດບໍ່ສຳເລັດ");
                                }
                              }}
                            />
                          </td>
                          <td className="p-2">
                            {prize.isConsolation ? (
                              <Badge variant="secondary">Consolation</Badge>
                            ) : (
                              <SimpleSelect
                                value={prize.drawMode ?? "instant"}
                                disabled={!canWrite}
                                onValueChange={async (v) => {
                                  try {
                                    await updatePrizeMutation.mutateAsync({
                                      eventId: detail.id,
                                      prizeId: prize.id,
                                      body: {
                                        drawMode: v as "instant" | "deferred",
                                      },
                                    });
                                  } catch {
                                    toast.error("ອັບເດດໂໝດບໍ່ສຳເລັດ");
                                  }
                                }}
                                options={[
                                  { value: "instant", label: "Instant" },
                                  { value: "deferred", label: "Deferred" },
                                ]}
                              />
                            )}
                          </td>
                          <td className="p-2">
                            {prize.isConsolation ? (
                              <Badge variant="secondary">Consolation</Badge>
                            ) : (
                              <Badge variant="outline">Prize</Badge>
                            )}
                          </td>
                          <td className="p-2">
                            <div className="flex items-center justify-end gap-1">
                              {canWrite &&
                              prize.drawMode === "deferred" &&
                              !prize.isConsolation &&
                              prize.remaining > 0 ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={deferredDrawMutation.isPending}
                                  onClick={async () => {
                                    try {
                                      const result =
                                        await deferredDrawMutation.mutateAsync({
                                          eventId: detail.id,
                                          prizeId: prize.id,
                                          count: 1,
                                        });
                                      toast.success(
                                        `ສຸ່ມໄດ້ ${result.drawn} ຄົນ (pool ${result.eligiblePool})`,
                                      );
                                    } catch {
                                      toast.error("ສຸ່ມ deferred ບໍ່ສຳເລັດ");
                                    }
                                  }}
                                >
                                  Draw 1
                                </Button>
                              ) : null}
                              {canWrite ? (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                                  disabled={deletePrizeMutation.isPending}
                                  title={
                                    (prize.claimed ?? 0) > 0
                                      ? "ມີຜູ້ຖືກລາງວັນແລ້ວ — ລຶບບໍ່ໄດ້"
                                      : "ລຶບລາງວັນ"
                                  }
                                  onClick={() => void handleDeletePrize(prize)}
                                >
                                  <Trash2Icon className="size-4" />
                                </Button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {canWrite ? (
                  <div className="flex flex-wrap items-end gap-2 border-t pt-4">
                    <div className="space-y-1">
                      <Label>ຊື່ລາງວັນໃໝ່</Label>
                      <Input
                        value={newPrizeName}
                        onChange={(e) => setNewPrizeName(e.target.value)}
                        placeholder="ຊື່ລາງວັນ"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label>ຈຳນວນ</Label>
                      <Input
                        className="w-24"
                        value={newPrizeQty}
                        onChange={(e) => setNewPrizeQty(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label>Weight</Label>
                      <Input
                        className="w-24"
                        value={newPrizeWeight}
                        onChange={(e) => setNewPrizeWeight(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label>ໂໝດ</Label>
                      <SimpleSelect
                        value={newPrizeMode}
                        onValueChange={(v) =>
                          setNewPrizeMode(v as "instant" | "deferred")
                        }
                        options={[
                          { value: "instant", label: "Instant" },
                          { value: "deferred", label: "Deferred" },
                        ]}
                      />
                    </div>
                    <Button
                      onClick={handleCreatePrize}
                      disabled={
                        createPrizeMutation.isPending || !newPrizeName.trim()
                      }
                    >
                      <PlusIcon className="mr-2 size-4" />
                      ເພີ່ມ
                    </Button>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </div>
        )}
      </Main>
    </>
  );
}
